/**
 * App shell: sticky left sidebar + main content area.
 *
 * Usage:
 *   <Layout>
 *     <PageHeader title="Leads" actions={...} />
 *     <YourContent />
 *   </Layout>
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { listMyPortals, type Role } from '../auth/api';
import { useTheme } from '../lib/theme';
import AskClaude from './AskClaude';
import { CommandPalette } from './CommandPalette';
import NotificationBell from './NotificationBell';
import { Icon, type IconName } from './Icon';
import { recordPageView } from '../api/page-views';

/** L-77 (Daniel 2026-07-04): sidebar sections. Items without a section
 *  render as top-level primary nav; items with a section render under
 *  a collapsible header. Admin collapses by default; other sections
 *  are open. Users' collapse state persists in localStorage. */

/**
 * Navigation model (Daniel 2026-07-27).
 *
 * The old rail had ~40 destinations in four flat lists. Three problems:
 * about a dozen entries were ACTIONS not places ("Submit client", "Import
 * closings"); nine marketing tools each took a top-level slot; and an
 * agent saw leadership pages they had no business opening.
 *
 * The model now:
 *   • `daily`  — the six things you touch every day. No section header,
 *                pinned to the top. If it needs a label it isn't daily.
 *   • `group`  — everything else, in one of four collapsible groups.
 *                Closed by default, state remembered per user.
 *   • Verbs live on the Create button and in search, not in this list.
 *
 * Role filtering is explicit via `roles`. An empty/absent `roles` means
 * every agent-portal user. This is a nav-level convenience only — the API
 * enforces the real permissions, because hiding a link is not security.
 */
/**
 * Sidebar groups (Build 36, Daniel 2026-09-20).
 *
 * The rail had five groups and two of them were junk drawers. Manage held
 * fifteen rows covering five unrelated jobs: somebody else's daily queues,
 * the people roster, coaching, configuration and diagnostics. Nothing was
 * ever found in there, because "manage" describes everything. Performance
 * held ten and mixed looking backward at numbers with deciding forward what
 * the year will be, which are different moods on different days.
 *
 * What changed:
 *   • Grow closed. Its rows went to Marketing, Recruiting and Settings.
 *   • Manage split into Team (the roster) and Operations (the coordinator's
 *     queues). Its eleven configuration rows became ONE Settings row.
 *   • Performance split into Performance (the plans and the numbers) and
 *     Coaching (the four pages that are about a person, not a figure).
 *   • Recruiting is new. The recruit packet flow already existed and had no
 *     front door, so the only way in was a menu that did something narrower.
 *   • Auctions left Grow. It is a line of business filed next to sign-up
 *     forms; it now sits on its own above the groups, owner only. It becomes
 *     a group of its own once it has three rows.
 *
 * Consult guides and document packets went back to Marketing on Daniel's
 * call (2026-09-20). Worth knowing they were moved OUT of Marketing on
 * 2026-07-29 for the opposite reason: they are made for one client on one
 * deal rather than aimed at an audience. Both readings are defensible and
 * the person using the rail picked. What is left in Tools is two genuine
 * utilities, which is a small group but an honest one.
 *
 * For the owner this is 42 destinations and 17 rows at rest, from 48 rows
 * across five headers that told you nothing.
 */
type NavGroup =
  | 'marketing'
  | 'tools'
  | 'recruiting'
  | 'performance'
  | 'coaching'
  | 'team'
  | 'operations';

const GROUP_ORDER: NavGroup[] = [
  'marketing', 'tools', 'recruiting', 'performance', 'coaching', 'team', 'operations',
];
const GROUP_LABEL: Record<NavGroup, string> = {
  marketing: 'Marketing',
  tools: 'Tools',
  recruiting: 'Recruiting',
  performance: 'Performance',
  coaching: 'Coaching',
  team: 'Team',
  operations: 'Operations',
};
const GROUP_ICON: Record<NavGroup, IconName> = {
  marketing: 'sparkles',
  tools: 'list-checks',
  recruiting: 'user',
  performance: 'trending-up',
  coaching: 'message-square',
  team: 'building-2',
  operations: 'clock',
};

/**
 * Role tiers for the nav (Build 36, Daniel 2026-09-20).
 *
 * There used to be one constant gating sixteen rows, which made Angel and
 * Rachelle the same person as far as the rail was concerned. They are not.
 * Rachelle runs the business; Angel coordinates transactions. Ad spend, the
 * funnel, the roster, one-on-ones and team health were all sitting in front
 * of a coordinator with no use for any of them.
 *
 * Three tiers now, widest last:
 *
 *   OWNER_ONLY  Daniel. The auction board and the export credential.
 *   OWNER_OPS   Daniel and Rachelle. Money, spend, the roster, coaching,
 *               the website, the lead connections.
 *   LEADERSHIP  those two plus admin. Submissions, lenders, buyer accounts,
 *               the diagnostics. The things a coordinator works in daily.
 *
 * V-3 still holds and is why this comment is long: `showing_assistant` is
 * deliberately in none of these and must never be added. That role is the
 * widest READ scope in the app and the tightest on money. Sophia holds it
 * alongside `agent`, so she sees the agent rail, which is the correct set.
 *
 * Mirrors TEAM_PRODUCTION_ROLES in apps/api/src/lib/auth.ts. As always: the
 * API enforces the real permissions. Hiding a link is not security.
 */
const OWNER_ONLY: Role[] = ['owner'];
const OWNER_OPS: Role[] = ['owner', 'operations'];
const LEADERSHIP: Role[] = ['owner', 'operations', 'admin'];

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Pinned to the top, no section header. */
  daily?: boolean;
  /** Which collapsible group this sits under when not daily. */
  group?: NavGroup;
  /** Restrict to these roles. Absent = every agent-portal user. */
  roles?: Role[];
  /** Shown only when the user is the team's transaction coordinator. */
  tcOnly?: boolean;
  /** Promote to a daily item for the TC; grouped for everyone else. */
  dailyForTc?: boolean;
  /** If set, shown only when user.portal matches. Defaults to 'agent'. */
  portals?: Array<'agent' | 'partner' | 'client'>;
  /** Per-portal label override — "My Deals" for partners, "Transactions"
   *  for agents. */
  labelByPortal?: Partial<Record<'agent' | 'partner' | 'client', string>>;
  /** Per-portal target — agents land on the Kanban board, partners and
   *  clients on the list (they have no Board page). */
  toByPortal?: Partial<Record<'agent' | 'partner' | 'client', string>>;
  /** Label swap for leadership. Used so the numbers page reads "My
   *  numbers" for an agent and "Team numbers" for owner/ops — the wording
   *  makes the permission obvious without a tooltip. */
  labelForLeadership?: string;
  /** Build 36: pinned BELOW the groups rather than above them. Settings is
   *  the only one, and it belongs at the bottom because it is the row you
   *  reach for least and should never be in the way of the ones you reach
   *  for most. */
  footer?: boolean;
  /** Build 36: a hairline above this row. Auctions uses it so a line of
   *  business does not read as the ninth daily item. */
  dividerBefore?: boolean;
  /** Build 36: other paths that should keep this row highlighted. Settings
   *  is a directory whose pages live at eleven unrelated routes, and a hub
   *  that goes dark the moment you use it is a hub you stop trusting. */
  alsoActiveFor?: string[];
}

const NAV: NavItem[] = [
  // ── Daily ────────────────────────────────────────────────────────
  // The things you touch without thinking. No header. If it needs a label
  // to justify its place here, it is not daily.
  { to: '/', label: 'Home', icon: 'home', daily: true, portals: ['agent'] },
  // Build 64: who to reach today, ranked, with the reason. The daily habit.
  { to: '/today', label: 'Today', icon: 'sun', daily: true, portals: ['agent'] },
  // M-10 (Daniel 2026-07-30): unified inbox — the work-to-zero list.
  { to: '/inbox', label: 'Inbox', icon: 'mail', daily: true, portals: ['agent'] },
  { to: '/leads', label: 'Leads', icon: 'users', daily: true, portals: ['agent'] },
  // Build 11 (2026-09-14): the follow-up loop, one tap from anywhere.
  // Build 64: Today lists every follow-up that is due, so the board moves
  // under Operations. The daily rail was at eleven rows, one past what the
  // eye takes in at once, and this was the row Today makes redundant.
  { to: '/leads/follow-ups', label: 'Follow-ups', icon: 'phone', group: 'operations', portals: ['agent'] },
  // AI drafts (2026-11-01): what Claude and Grok left for Daniel or Rachelle to accept.
  { to: '/ai-drafts', label: 'AI drafts', icon: 'sparkles', group: 'operations', portals: ['agent'], roles: OWNER_OPS },
  {
    to: '/transactions',
    label: 'Deals',
    icon: 'briefcase',
    daily: true,
    portals: ['agent', 'partner', 'client'],
    labelByPortal: { partner: 'My deals', client: 'My deal' },
    toByPortal: { agent: '/transactions/board' },
  },
  // Lender portal only — buyers assigned to this lender for pre-approval.
  { to: '/pre-approvals', label: 'Pre-approvals', icon: 'users', daily: true, portals: ['partner'] },
  { to: '/tasks', label: 'Tasks', icon: 'list-checks', daily: true, portals: ['agent'] },
  { to: '/calendar', label: 'Calendar', icon: 'calendar', daily: true, portals: ['agent'] },
  { to: '/contacts', label: 'Contacts', icon: 'phone', daily: true, portals: ['agent'] },

  // ── The auction board ────────────────────────────────────────────
  //
  // Build 36 (Daniel 2026-09-20): out of Grow, where it sat between smart
  // plans and sign-up forms. It is a line of business, not a growth tool,
  // and it is the one TR is building its own platform for. Owner only for
  // now, by Daniel's call. When it has three rows it becomes a group.
  {
    to: '/online-auctions',
    label: 'Auctions',
    icon: 'building-2',
    daily: true,
    dividerBefore: true,
    roles: OWNER_ONLY,
  },

  // ── TC working queues ────────────────────────────────────────────
  //
  // Build 36: `tcOnly` finally used. These are Angel's morning page and her
  // Tuesday seller-update drafts. They were in every agent's rail under
  // Manage, because `dailyForTc` only ever PROMOTED them for her and never
  // hid them from anybody else. The flag to do that existed and was unused
  // since the day it was written.
  // Build 64: renamed from "Today", which now names the agent's daily list.
  { to: '/tc-today', label: 'TC Today', icon: 'clock', dailyForTc: true, tcOnly: true, group: 'operations' },
  { to: '/weekly-drafts', label: 'Tuesday drafts', icon: 'mail', dailyForTc: true, tcOnly: true, group: 'operations' },
  // Weekly one-page market update per active listing. Agents see their own
  // listings, the TC and leadership see all of them.
  { to: '/seller-updates', label: 'Seller updates', icon: 'trending-up', dailyForTc: true, group: 'operations', portals: ['agent'] },

  // ── Marketing ────────────────────────────────────────────────────
  //
  // Order is the order you use them: see where you stand, make the thing,
  // schedule it, then the per-client deliverables, then the brand and the
  // site those all come from.
  { to: '/marketing', label: 'Studio', icon: 'sparkles', group: 'marketing' },
  { to: '/marketing/create', label: 'Create', icon: 'pencil', group: 'marketing' },
  // Build 36: was "Calendar", which is also the name of a daily item four
  // rows above it. Two identical labels in one rail is a coin toss.
  { to: '/marketing/calendar', label: 'Content calendar', icon: 'calendar', group: 'marketing' },
  { to: '/marketing/graphics', label: 'Graphics', icon: 'layers', group: 'marketing' },
  // Build 36: moved from Grow, which closed.
  { to: '/plans', label: 'Smart plans', icon: 'list-checks', group: 'marketing' },
  // Build 36: back to Marketing on Daniel's call. See the note above
  // NavGroup for why they left in July.
  { to: '/tools/consult-guides', label: 'Consult guides', icon: 'file-text', group: 'marketing' },
  // Build 37 (Daniel 2026-09-20): open to agents. It is a per-client
  // deliverable exactly like consult guides beside it, and routing every
  // agent's client packet through leadership was a bottleneck, not a
  // control. The page and the API decide what any one caller may put in
  // it; this row only decides who is offered the door.
  { to: '/tools/document-packets', label: 'Document packets', icon: 'briefcase', group: 'marketing' },
  { to: '/marketing/brand', label: 'Brand', icon: 'building-2', group: 'marketing', roles: OWNER_OPS },
  // IDX-3 (Daniel 2026-08-13): the public MLS search pages on
  // turnkeywichita.com, managed here so a new page never needs a deploy.
  { to: '/marketing/site-searches', label: 'Site searches', icon: 'search', group: 'marketing', roles: OWNER_OPS },

  // ── Tools ────────────────────────────────────────────────────────
  // Two utilities. Small group, honest name.
  { to: '/investor-analyzer', label: 'Deal analyzer', icon: 'trending-up', group: 'tools' },
  // M-14 (Daniel 2026-07-30): drop any audio recording → Whisper text,
  // optionally logged to a contact's timeline.
  { to: '/tools/transcribe', label: 'Transcribe', icon: 'message-square', group: 'tools' },

  // ── Recruiting ───────────────────────────────────────────────────
  //
  // Build 36 (Daniel 2026-09-20). The recruit packet already builds the
  // same three sheets one of our own agents builds — Life by Design, the
  // annual plan, the economic model — saved together and printed as one
  // PDF with their name on it. That is a recruit sitting across the table
  // building the year they would have here, in our engine. It had no menu
  // entry at all; the only way in was the "+ New recruit" button on a page
  // you had to already be on.
  //
  // The income projector is the previous generation: same economic model
  // math, one screen instead of three, and its own saved rows. It is listed
  // so those rows are not stranded, not because it is the way in.
  { to: '/recruits', label: 'Recruit plans', icon: 'user', group: 'recruiting', roles: OWNER_OPS },

  // ── Performance ──────────────────────────────────────────────────
  //
  // Plans first, then the numbers that judge them. Every icon in this group
  // is different from every other, which was not true before: seven rows in
  // the old rail shared 'trending-up' and five of them were right here, so
  // the icons carried no information at the moment you were scanning.
  //
  // R-5 (Daniel 2026-08-04): annual business plan sits above the 12WY.
  { to: '/business-plan', label: 'Business plan', icon: 'layers', group: 'performance' },
  { to: '/12-week-year', label: '12 week year', icon: 'circle-check', group: 'performance' },
  // V-2 (Daniel 2026-08-10): "Team numbers" for everybody. The page opens in
  // the team view for everyone and carries its own My / Team toggle.
  { to: '/production', label: 'Team numbers', icon: 'trending-up', group: 'performance' },
  // Build 10 (Daniel 2026-09-08): any window, any comparison, every number
  // drills to the deals behind it. The API scopes money per caller.
  { to: '/reports', label: 'Reports', icon: 'search', group: 'performance' },
  // Build 36: Comp ledger (Build 31) shipped into the router and into no
  // menu. For ten days the only way to open it was to type the URL.
  { to: '/comp-ledger', label: 'Comp ledger', icon: 'file-text', group: 'performance', roles: OWNER_OPS },
  { to: '/funnel', label: 'Funnel', icon: 'arrow-up-right', group: 'performance', roles: OWNER_OPS },
  // Audit H6 (2026-08-02): ad_name was stored and never reported on.
  { to: '/ad-performance', label: 'Ad performance', icon: 'sparkles', group: 'performance', roles: OWNER_OPS },
  // F-1 (Daniel 2026-08-04): live bank feed + MREA P&L. Bank balances are
  // owner/operations business and admin does not get a peek just because
  // the role exists. The API enforces the same pair with requireRole.
  { to: '/finances', label: 'Finances', icon: 'briefcase', group: 'performance', roles: OWNER_OPS },

  // ── Coaching ─────────────────────────────────────────────────────
  //
  // Build 36: the four pages that are about a person rather than a figure.
  // You open these in a different meeting than you open Reports.
  //
  // R11-4 (Daniel 2026-08-12): no role gate on the scorecard on purpose.
  // Agents land on their own sheet; leadership gets the team sheet and the
  // per-agent picker. The API scopes what each caller may read.
  { to: '/production/scorecard', label: 'Coach scorecard', icon: 'list-checks', group: 'coaching' },
  // No role gate: an agent hears their own calls for self-review and
  // leadership can filter to anyone. Again, the API enforces the split.
  { to: '/call-review', label: 'Call review', icon: 'phone', group: 'coaching' },
  { to: '/one-on-ones', label: 'One-on-ones', icon: 'users', group: 'coaching', roles: OWNER_OPS },
  // Coaching payments (2026-10-30). No role gate: agents see their own
  // months; Daniel and Rachelle see everyone. The API enforces the split.
  { to: '/coaching', label: 'Coaching payments', icon: 'briefcase', group: 'coaching' },
  { to: '/production/health', label: 'Team health', icon: 'shield-check', group: 'coaching', roles: OWNER_OPS },

  // ── Team ─────────────────────────────────────────────────────────
  // Who the people are. Not how they are doing, which is Coaching.
  { to: '/team', label: 'Team', icon: 'building-2', group: 'team', roles: OWNER_OPS },
  { to: '/vendors', label: 'Vendors', icon: 'briefcase', group: 'team' },
  { to: '/partners', label: 'Lenders', icon: 'users', group: 'team', roles: LEADERSHIP },
  // BUY-2 (Daniel 2026-08-18): the roster of site-signup logins. Per-contact
  // buyer activity lives on the contact page; this is the team-wide list.
  { to: '/admin/buyer-accounts', label: 'Buyer accounts', icon: 'user', group: 'team', roles: LEADERSHIP },

  // ── Operations ───────────────────────────────────────────────────
  // The coordinator's desk. Angel's two queues are pinned to the top of her
  // rail by dailyForTc and hidden from everyone who is not her by tcOnly;
  // they appear in this group for leadership only.
  { to: '/submit/inbox', label: 'Submissions', icon: 'file-text', group: 'operations', roles: LEADERSHIP },

  // ── Settings ─────────────────────────────────────────────────────
  //
  // Build 36: eleven rows became one.
  //
  // Website, Email prefs, Automations, Documents, Agent pages, Task
  // templates, Sign-up forms, Lead sources, Quo health, AI usage and Export
  // tokens were each taxing every person's eyes every day to serve a thing
  // done twice a year. Not one page moved. /settings is a directory that
  // shows a person the sections they can actually change.
  {
    to: '/settings',
    label: 'Settings',
    icon: 'settings',
    footer: true,
    alsoActiveFor: [
      '/templates', '/document-templates', '/forms',
      '/integrations/meta', '/integrations/lead-email', '/admin/lead-routing', '/admin/agents', '/admin/automations',
      '/admin/quo-health', '/admin/usage', '/admin/errors', '/admin/export-tokens', '/admin/ai-connections',
      '/admin/page-views',
    ],
  },

  // Deliberately NOT in the nav — these are actions, or one-time passes,
  // reached from the Create button, a page they belong to, or search:
  //   /submit, /submit/* forms, /admin/import-closings, /leads/new,
  //   /transactions/new, /contacts/new, /contacts/anniversaries,
  //   /contacts/duplicates, /contacts/nurture, /contacts/call-sheets
  //   (now tabs on Contacts), /contacts/dialer (opened from a call sheet
  //   or from Start calling on the contacts list), and /admin/deal-origins,
  //   which is a one-time backfill linked from the Comp ledger page
  //   because that is the only screen where its output matters.
];

/**
 * Build 39 (Daniel 2026-09-20): the nav as data, for the page-view summary.
 *
 * That page's whole job is the difference between "rows we claim are worth a
 * slot" and "rows anybody opened". It has to read the first list from the
 * same array the sidebar renders, or the two drift and the answer is wrong
 * in the direction that looks fine.
 */
export const NAV_TARGETS: Array<{ to: string; label: string }> =
  NAV.filter((n) => (n.portals ?? ['agent']).includes('agent'))
    .map((n) => ({ to: n.to, label: n.label }));

/**
 * Does this nav path cover this URL? (Build 36)
 *
 * Segment-aware on purpose. A plain startsWith would have '/team' match
 * '/templates', which is the kind of bug that only shows up once somebody
 * adds a route whose name happens to start with another one.
 */
function pathMatches(to: string, path: string): boolean {
  const base = to.split('?')[0];
  if (base === '/') return path === '/';
  return path === base || path.startsWith(base + '/');
}

/** Quick-create menu — the verbs that used to sit in the nav. */
const CREATE_ACTIONS: Array<{ to: string; label: string; icon: IconName; roles?: Role[] }> = [
  { to: '/leads/new', label: 'New lead', icon: 'users' },
  { to: '/transactions/new', label: 'New deal', icon: 'briefcase' },
  { to: '/contacts/new', label: 'New contact', icon: 'phone' },
  { to: '/submit', label: 'Submit a client', icon: 'file-text' },
  { to: '/submit/buyer-offer', label: 'Submit an offer', icon: 'file-text' },
  { to: '/production', label: 'Log my numbers', icon: 'trending-up' },
  { to: '/admin/import-closings', label: 'Import closings', icon: 'file-text', roles: LEADERSHIP },
];

/**
 * JS-driven mobile detection (Daniel 2026-06-07).
 *
 * CSS-only @media queries were unreliable in this app for reasons we
 * never fully pinned down — possibly aggressive CDN caching, possibly
 * Tailwind v4 layering, possibly the inline width:232 on Sidebar
 * winning against !important rules in some Safari paint paths. So we
 * just read the viewport width in JS and let React conditionally
 * render the sidebar entirely. Guaranteed to fire regardless of CSS
 * state.
 */
/**
 * Portal switcher (Daniel 2026-07-27). Login binds the JWT to the user's
 * MOST-PRIVILEGED role's portal, so an owner who is also set up as a lender
 * could never reach the partner portal to check it. Renders only when the
 * user genuinely has more than one portal.
 */
const PORTAL_LABEL: Record<string, string> = {
  agent: 'Team view',
  partner: 'Lender view',
  client: 'Client view',
};
function PortalSwitcher() {
  const { user, switchToPortal } = useAuth();
  const { data } = useQuery({
    queryKey: ['my-portals'],
    queryFn: listMyPortals,
    staleTime: 5 * 60_000,
    enabled: !!user,
  });
  const portals = data?.portals ?? [];
  if (portals.length < 2) return null;

  return (
    <select
      value={user?.portal ?? ''}
      onChange={(e) => {
        const next = e.currentTarget.value;
        if (next && next !== user?.portal) void switchToPortal(next);
      }}
      title="Switch portal"
      style={{
        // Lives inside the dark user card at the foot of the rail, so it
        // takes chrome colours rather than page colours.
        marginTop: 3,
        fontSize: 11,
        padding: '3px 5px',
        maxWidth: '100%',
        border: '1px solid rgba(255,255,255,0.16)',
        borderRadius: 5,
        background: 'rgba(255,255,255,0.08)',
        color: 'var(--chrome-ink)',
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {portals.map((p) => (
        <option key={`${p.team_id}:${p.portal}`} value={p.portal}>
          {PORTAL_LABEL[p.portal] ?? p.portal}
        </option>
      ))}
    </select>
  );
}

/** Shared responsive hook. Exported so pages can collapse their own
 *  layouts on phones, not just the app shell. (Daniel 2026-07-27.) */
export function useIsMobile(breakpoint = 900): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= breakpoint;
  });
  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [breakpoint]);
  return isMobile;
}

export default function Layout({ children, maxWidth = 1280 }: { children: React.ReactNode; maxWidth?: number }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const isMobile = useIsMobile();

  // Desktop collapse: hide the sidebar to give pages full width; a thin
  // hover strip at the far left slides it back as an overlay. Pinned state
  // persists in localStorage. (Daniel 2026-07-18)
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('tr-sidebar-collapsed') === '1'; } catch { return false; }
  });
  const [hovering, setHovering] = useState(false);
  useEffect(() => {
    try { localStorage.setItem('tr-sidebar-collapsed', collapsed ? '1' : '0'); } catch { /* ignore */ }
  }, [collapsed]);
  useEffect(() => { setHovering(false); }, [location.pathname]);

  const collapseBtn: React.CSSProperties = {
    position: 'absolute', top: 14, right: 8, zIndex: 5,
    width: 26, height: 26, borderRadius: 6, cursor: 'pointer',
    border: '1px solid var(--border)', background: 'var(--bg-surface)',
    color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 15, lineHeight: 1,
  };

  // Build 39: count this page view.
  //
  // One fire-and-forget POST per navigation. It is deliberately NOT in the
  // sidebar component's render path in any way that could block: the call
  // returns void, swallows its own errors, and nothing reads its result. A
  // counter that can break a page load is a counter that gets ripped out
  // the first time it does.
  //
  // Keyed on pathname only. The query string says what you filtered, not
  // what you opened, and re-counting a page because a filter changed would
  // make the busiest pages look busier than they are.
  useEffect(() => {
    if (!user) return;
    recordPageView(location.pathname);
  }, [user, location.pathname]);

  // Close the mobile sidebar whenever the route changes (clicking a nav link
  // should dismiss the overlay).
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Flag the body so global CSS can add bottom padding to .tr-main on
  // mobile (so content doesn't get hidden under the fixed bottom nav).
  // Only flagged for the agent portal — portals get a different shell.
  useEffect(() => {
    const isAgent = user?.portal === 'agent';
    if (isAgent) {
      document.body.classList.add('tr-has-bottom-nav');
    } else {
      document.body.classList.remove('tr-has-bottom-nav');
    }
    return () => document.body.classList.remove('tr-has-bottom-nav');
  }, [user?.portal]);

  // Show the sidebar in the layout flex flow ONLY on desktop. On mobile,
  // the only time we render Sidebar is as a fixed overlay when the user
  // has tapped the hamburger.
  const showSidebarInFlow = !isMobile;
  const showSidebarOverlay = isMobile && mobileOpen;

  return (
    <div
      className={mobileOpen ? 'tr-sidebar-open' : ''}
      style={{
        display: 'flex',
        // ROOT CAUSE of the "content pushed right" bug (Daniel 2026-06-07):
        // outer container defaulted to flex-direction:row. On mobile the
        // sticky MobileTopbar became a flex sibling of <main>, claiming
        // ~half the row width for itself (sized to its content's natural
        // width) and leaving <main> only the other half. Switching to
        // column on mobile stacks the topbar above main full-width.
        flexDirection: isMobile ? 'column' : 'row',
        minHeight: '100vh',
        background: 'var(--bg-page)',
        // Hard-cap the outer container so a runaway child can never push
        // content offscreen-right.
        ...(isMobile ? { width: '100vw', maxWidth: '100vw', overflowX: 'hidden' } : {}),
      }}
    >
      {/* Mobile top bar — only render on mobile. */}
      {isMobile && (
        <MobileTopbar
          portal={user?.portal ?? 'agent'}
          onToggle={() => setMobileOpen((v) => !v)}
          isOpen={mobileOpen}
        />
      )}

      {/* Sidebar in normal flex flow — desktop, when not collapsed. */}
      {showSidebarInFlow && !collapsed && (
        <div style={{ position: 'relative', flexShrink: 0, display: 'flex', minWidth: 232, maxWidth: 280 }}>
          <Sidebar />
          <button onClick={() => setCollapsed(true)} title="Collapse sidebar" aria-label="Collapse sidebar" style={collapseBtn}>«</button>
        </div>
      )}

      {/* Collapsed — thin hover strip + slide-in overlay. */}
      {showSidebarInFlow && collapsed && (
        <>
          <div
            onMouseEnter={() => setHovering(true)}
            aria-hidden="true"
            style={{ position: 'fixed', left: 0, top: 0, width: 14, height: '100vh', zIndex: 120 }}
          />
          <div
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            style={{
              position: 'fixed', left: 0, top: 0, height: '100vh', width: 264, zIndex: 130,
              transform: hovering ? 'translateX(0)' : 'translateX(-264px)',
              transition: 'transform 180ms cubic-bezier(0.16,1,0.3,1)',
              boxShadow: hovering ? '4px 0 24px rgba(0,0,0,0.18)' : 'none',
              display: 'flex',
            }}
          >
            <Sidebar />
            <button onClick={() => { setCollapsed(false); setHovering(false); }} title="Pin sidebar open" aria-label="Pin sidebar" style={collapseBtn}>»</button>
          </div>
        </>
      )}

      {/* Sidebar as fixed-position overlay — mobile when open. */}
      {showSidebarOverlay && (
        <>
          <div
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 14, 34, 0.45)',
              zIndex: 150,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: 'min(280px, 80vw)',
              height: '100vh',
              zIndex: 200,
              boxShadow: '4px 0 24px rgba(0, 0, 0, 0.18)',
              animation: 'tr-sidebar-slide-in 220ms cubic-bezier(0.16, 1, 0.3, 1)',
              // Transparent on purpose: the aside inside paints the dark
              // chrome gradient. A white wrapper here showed through as a
              // stripe wherever the two widths disagreed.
              background: 'transparent',
            }}
          >
            <Sidebar />
          </div>
        </>
      )}

      <main
        key={location.pathname}
        className="tr-page-anim tr-main"
        style={{
          flex: 1,
          minWidth: 0,
          ...(isMobile
            ? {
                padding: '16px 14px 88px',
                width: '100%',
                maxWidth: '100%',
                margin: 0,
              }
            : {
                padding: '32px 40px 64px',
                maxWidth,
                margin: '0 auto',
                width: '100%',
              }),
        }}
      >
        {/* Build 12 (Daniel 2026-09-14): the coach seat reads everything and
            saves nothing. Say so on every page rather than letting the first
            Save button explain it with a 403. */}
        {user?.coach && (
          <div
            role="status"
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'var(--brand-primary-soft)',
              border: '1px solid var(--brand-primary)',
              color: 'var(--text-heading)',
              fontSize: 13,
            }}
          >
            <strong>Coach view.</strong> You can see everything the owner sees. Nothing here can be changed from this seat.
          </div>
        )}
        {children}
      </main>
      <CommandPalette />
      {/* Floating "Ask Claude" help button + side panel. Available on
          every authenticated page — agents can ask "how do I add a
          co-agent?" and get context-aware answers from Claude. */}
      <AskClaude />
      {/* Mobile-only bottom navigation. Hidden on desktop via the
          .tr-bottom-nav class. Sticky to bottom of viewport, respects
          safe-area-inset for iPhones with home indicators. */}
      {user?.portal === 'agent' && (
        <MobileBottomNav onOpenSidebar={() => setMobileOpen(true)} />
      )}
    </div>
  );
}

/** Bottom navigation bar — primary destinations. Renders only on mobile
 *  via CSS media query; on desktop the tr-bottom-nav class collapses to
 *  display:none.
 *
 *  Note: the "More" hamburger that used to live here was removed in favor
 *  of a direct "#'s" → /production tab (Daniel 2026-06-07). Daily number
 *  reporting is the highest-frequency mobile task for the TurnKey team
 *  this quarter, so it earns a thumb-reach slot. Full sidebar is still
 *  accessible via the hamburger in the MobileTopbar.
 */
function MobileBottomNav(_props: { onOpenSidebar: () => void }) {
  const location = useLocation();
  // Five-icon thumb nav: Home, People, Deals, Tasks, #'s. Five tabs is
  // the upper limit before icons get tap-target-cramped on a 375px iPhone.
  const items: Array<{ to: string; label: string; icon: IconName; matches: (path: string) => boolean }> = [
    { to: '/today', label: 'Today', icon: 'sun', matches: (p) => p === '/today' },
    { to: '/contacts', label: 'People', icon: 'users', matches: (p) => p.startsWith('/contacts') },
    // L-31: mobile thumb nav follows the same Kanban-first rule as the
    // desktop sidebar for the Deals tab.
    { to: '/transactions/board', label: 'Deals', icon: 'briefcase', matches: (p) => p.startsWith('/transactions') },
    { to: '/tasks', label: 'Tasks', icon: 'list-checks', matches: (p) => p.startsWith('/tasks') },
    { to: '/production', label: "#'s", icon: 'trending-up', matches: (p) => p.startsWith('/production') },
  ];
  return (
    <nav className="tr-bottom-nav" aria-label="Primary">
      {items.map((item) => {
        const active = item.matches(location.pathname);
        return (
          <Link key={item.to} to={item.to} className={active ? 'is-active' : ''}>
            <Icon name={item.icon} size={24} />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Mobile-only top bar: hamburger button + brand title. Hidden on desktop via
 * the .tr-topbar class media query in styles/index.css.
 */
function MobileTopbar({
  portal,
  onToggle,
  isOpen,
}: {
  portal: 'agent' | 'partner' | 'client';
  onToggle: () => void;
  isOpen: boolean;
}) {
  return (
    <div
      className="tr-topbar"
      style={{
        position: 'sticky',
        top: 0,
        left: 0,
        right: 0,
        // Above the drawer (z-200) so the hamburger stays tappable while
        // the menu is open — previously the backdrop swallowed it and the
        // close icon was unreachable.
        zIndex: 210,
        alignItems: 'center',
        gap: 12,
        padding: '10px 14px',
        // Dark to match the rail. A white bar directly above a near-black
        // drawer read as two different apps.
        background: 'var(--chrome-flat)',
        borderBottom: '1px solid var(--chrome-line)',
        // display:flex when shown — overridden to none above 720px.
      }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
        style={{
          background: 'transparent',
          border: '1px solid rgba(255,255,255,0.16)',
          borderRadius: 8,
          padding: '6px 12px',
          minWidth: 44,
          minHeight: 40,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
          color: 'var(--chrome-ink)',
          fontFamily: 'inherit',
        }}
      >
        <Icon name={isOpen ? 'x' : 'panel-left'} size={17} />
      </button>
      {/* Bell also lives out here on mobile. The rail is a closed drawer on a
          phone, so a bell that only exists inside it is a bell you never see
          — and the badge is most of its value. Agent portal only, matching
          where the notifications themselves are scoped. */}
      {portal === 'agent' && <NotificationBell />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: 15,
            color: 'var(--chrome-ink)',
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
          }}
        >
          {portal === 'partner' ? 'Lender portal' : portal === 'client' ? 'Client portal' : 'TurnKey'}
        </div>
      </div>
    </div>
  );
}

function Sidebar() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [theme, , toggleTheme] = useTheme();
  const [createOpen, setCreateOpen] = useState(false);

  const portal = user?.portal ?? 'agent';
  const roles = user?.roles ?? [];
  const isLeadership = roles.some((r) => LEADERSHIP.includes(r));
  const isTc = !!user?.is_tc;

  const visible = NAV.filter((n) => {
    const allowed = n.portals ?? ['agent'];
    if (!allowed.includes(portal)) return false;
    if (n.tcOnly && !isTc) return false;
    if (n.roles && !n.roles.some((r) => roles.includes(r))) return false;
    return true;
  });

  const labelFor = (item: NavItem) =>
    item.labelByPortal?.[portal]
    ?? (isLeadership && item.labelForLeadership ? item.labelForLeadership : item.label);

  // A TC's working queues get promoted out of Manage and pinned to the top.
  const isDaily = (n: NavItem) => n.daily || (n.dailyForTc && isTc);
  const dailyItems = visible.filter(isDaily);
  // Build 36: pinned to the bottom, below the groups.
  const footerItems = visible.filter((n) => n.footer && !isDaily(n));

  // Build 36: one key per group in the union. Miss one and its rows are
  // filtered, counted, and then pushed onto undefined.
  const byGroup: Record<NavGroup, NavItem[]> = {
    marketing: [], tools: [], recruiting: [],
    performance: [], coaching: [], team: [], operations: [],
  };
  for (const item of visible) {
    if (isDaily(item)) continue;
    if (item.group) byGroup[item.group].push(item);
  }

  // Build 36: the LONGEST matching path wins.
  //
  // This was a bare startsWith, which meant '/leads' matched
  // '/leads/follow-ups' and both rows lit up at once. Same for Studio on
  // every /marketing page, and for Team numbers on the scorecard and on
  // team health. Three rows highlighted is the rail telling you you are in
  // three places, which is worse than telling you nothing.
  //
  // A child route still keeps its parent highlighted when the parent is the
  // only match, which is the behaviour the old code was reaching for.
  const activeTo = useMemo(() => {
    let best: string | null = null;
    let bestLen = -1;
    for (const n of visible) {
      for (const p of [n.to, ...(n.alsoActiveFor ?? [])]) {
        if (!pathMatches(p, location.pathname)) continue;
        // Length of the MATCHED path, not of the row's own target, so a row
        // that claims a long alsoActiveFor path wins there without also
        // winning on its own short one.
        const len = p.length;
        if (len > bestLen) { bestLen = len; best = n.to; }
      }
    }
    return best;
  }, [visible, location.pathname]);
  const isActive = (item: NavItem) => item.to === activeTo;

  // Group open state persists per user. A group force-opens when the
  // current route is one of its children, so you can always see where
  // you are even if you'd collapsed it.
  const [openGroups, setOpenGroups] = useState<Set<NavGroup>>(() => {
    try {
      const raw = localStorage.getItem('tr_nav_groups_open');
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return new Set(arr as NavGroup[]);
      }
    } catch { /* ignore */ }
    return new Set<NavGroup>();
  });
  function toggleGroup(g: NavGroup) {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g); else next.add(g);
      try { localStorage.setItem('tr_nav_groups_open', JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      return next;
    });
  }

  const createActions = CREATE_ACTIONS.filter(
    (a) => portal === 'agent' && (!a.roles || a.roles.some((r) => roles.includes(r))),
  );

  function renderItem(item: NavItem, nested = false) {
    const targetTo = item.toByPortal?.[portal] ?? item.to;
    const active = isActive(item);
    return (
      <Link
        key={item.to}
        to={targetTo}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: nested ? '7px 9px 7px 34px' : '8px 9px',
          marginBottom: 1,
          borderRadius: 'var(--radius)',
          textDecoration: 'none',
          color: active ? '#fff' : nested ? 'var(--chrome-dim)' : 'var(--chrome-muted)',
          background: active ? 'var(--chrome-active)' : 'transparent',
          boxShadow: active ? 'inset 0 1px 0 rgba(255,255,255,0.10)' : 'none',
          fontSize: nested ? 13 : 13.5,
          fontWeight: active ? 600 : 450,
          transition: 'background 130ms var(--ease), color 130ms var(--ease)',
        }}
        onMouseEnter={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'var(--chrome-hover)';
            e.currentTarget.style.color = 'var(--chrome-ink)';
          }
        }}
        onMouseLeave={(e) => {
          if (!active) {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = nested ? 'var(--chrome-dim)' : 'var(--chrome-muted)';
          }
        }}
      >
        {!nested && <Icon name={item.icon} size={15} />}
        {labelFor(item)}
      </Link>
    );
  }

  return (
    <aside
      className="tr-sidebar"
      style={{
        width: '100%',
        minWidth: 214,
        maxWidth: 260,
        background: 'var(--chrome-bg)',
        boxShadow: 'var(--chrome-edge)',
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        padding: '13px 10px 10px',
      }}
    >
      {/* Wordmark. The KW red square is the ONLY place brand red appears
          in the app — see tokens.css rule 4. */}
      <button
        onClick={() => navigate('/')}
        style={{
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '5px 8px 6px', background: 'transparent', border: 'none',
          cursor: 'pointer', textAlign: 'left', color: 'var(--chrome-ink)',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 20, height: 20, flexShrink: 0, borderRadius: 5,
            border: '2px solid currentColor', position: 'relative',
          }}
        >
          <span style={{
            position: 'absolute', left: 2.5, bottom: 2.5, width: 6, height: 6,
            background: 'var(--kw-red)',
          }} />
        </span>
        <span style={{
          fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14.5,
          letterSpacing: '-0.035em',
        }}>
          {portal === 'partner' ? 'Lender portal' : portal === 'client' ? 'Client portal' : 'TurnKey'}
        </span>
      </button>

      {/* Search everything: a visible bar, not a small icon. Daniel 2026-10-16
          asked for search across the CRM. The palette (contacts, deals, leads,
          tasks, documents) already existed behind a 31px icon nobody saw. */}
      {portal === 'agent' && (
        <div style={{ padding: '8px 4px 0' }}>
          <button
            type="button"
            title="Search everything (⌘K)"
            aria-label="Search everything"
            onClick={() => window.dispatchEvent(new CustomEvent('tr:open-command-palette'))}
            style={{
              width: '100%', height: 31, borderRadius: 'var(--radius)', border: 'none',
              background: 'rgba(255,255,255,0.08)', color: 'var(--chrome-ink)',
              fontFamily: 'inherit', fontSize: 12.5, cursor: 'pointer', textAlign: 'left',
              display: 'flex', alignItems: 'center', gap: 8, padding: '0 10px',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
          >
            <Icon name="search" size={14} />
            <span style={{ flex: 1, opacity: 0.85 }}>Search everything</span>
            <span style={{ fontSize: 11, opacity: 0.55 }}>⌘K</span>
          </button>
        </div>
      )}

      {/* Create + search. Every verb that used to be a nav row lives here. */}
      {portal === 'agent' && (
        <div style={{ position: 'relative', display: 'flex', gap: 6, padding: '8px 4px 12px' }}>
          <button
            type="button"
            onClick={() => setCreateOpen((v) => !v)}
            aria-expanded={createOpen}
            style={{
              flex: 1, height: 31, borderRadius: 'var(--radius)', border: 'none',
              background: '#fff', color: '#0A0A0A', fontFamily: 'inherit',
              fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              transition: 'background 130ms var(--ease)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#E8E8E6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
          >
            <Icon name="plus" size={14} /> Create
          </button>
          {/* Bell sits up here beside Create and search, not down in the
              user block at the foot of the rail. Anchored to the bottom of
              a 100vh sidebar, its panel would have opened off the bottom of
              the screen. (NB1, 2026-08-01) */}
          <NotificationBell />

          {createOpen && (
            <>
              <div
                onClick={() => setCreateOpen(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 200 }}
              />
              <div
                style={{
                  // Sits below the trigger. 52 not 42: on mobile the global
                  // 44px min-height stretches the Create button, and at 42
                  // the menu covered its own trigger.
                  position: 'absolute', top: 52, left: 4, right: 4, zIndex: 201,
                  // Dark surface — a white card here was blinding against
                  // the rail, and its black shadow was invisible.
                  background: '#23232A', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: '0 12px 34px rgba(0,0,0,0.5)',
                  padding: 5, overflow: 'hidden',
                }}
              >
                {createActions.map((a) => (
                  <Link
                    key={a.to + a.label}
                    to={a.to}
                    onClick={() => setCreateOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 9,
                      minHeight: 38,
                      padding: '8px 9px', borderRadius: 'var(--radius-sm)',
                      color: 'var(--chrome-ink)', textDecoration: 'none', fontSize: 13,
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Icon name={a.icon} size={14} />
                    {a.label}
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      <nav style={{ flex: 1, overflowY: 'auto', margin: '0 -4px', padding: '0 4px' }}>
        {dailyItems.map((item) => (
          // Build 36: a hairline above a pinned row that is a line of
          // business rather than a daily habit. Auctions is the only one.
          item.dividerBefore ? (
            <div key={`d-${item.to}`}>
              <div style={{ height: 1, background: 'var(--chrome-line)', margin: '9px 9px' }} />
              {renderItem(item)}
            </div>
          ) : renderItem(item)
        ))}

        {GROUP_ORDER.some((g) => byGroup[g].length > 0) && (
          <div style={{ height: 1, background: 'var(--chrome-line)', margin: '9px 9px' }} />
        )}

        {GROUP_ORDER.map((g) => {
          const items = byGroup[g];
          if (items.length === 0) return null;
          const hasActive = items.some(isActive);
          const open = openGroups.has(g) || hasActive;
          return (
            <div key={g}>
              <button
                type="button"
                onClick={() => toggleGroup(g)}
                aria-expanded={open}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                  padding: '7px 9px', marginBottom: 1, borderRadius: 'var(--radius)',
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  textAlign: 'left', fontFamily: 'inherit', fontSize: 13.5,
                  color: hasActive ? 'var(--chrome-ink)' : 'var(--chrome-muted)',
                  fontWeight: hasActive ? 500 : 400,
                  transition: 'background 130ms var(--ease), color 130ms var(--ease)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--chrome-hover)';
                  e.currentTarget.style.color = 'var(--chrome-ink)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = hasActive ? 'var(--chrome-ink)' : 'var(--chrome-muted)';
                }}
              >
                <Icon name={GROUP_ICON[g]} size={15} />
                <span style={{ flex: 1 }}>{GROUP_LABEL[g]}</span>
                <Icon
                  name="chevron-right"
                  size={13}
                  style={{
                    opacity: 0.55,
                    transition: 'transform 160ms var(--ease)',
                    transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
                  }}
                />
              </button>
              {open && items.map((item) => renderItem(item, true))}
            </div>
          );
        })}

        {/* Build 36: Settings, below everything. The row you reach for least
            should never sit above the ones you reach for most. */}
        {footerItems.length > 0 && (
          <div style={{ height: 1, background: 'var(--chrome-line)', margin: '9px 9px' }} />
        )}
        {footerItems.map((item) => renderItem(item))}
      </nav>

      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 9,
          padding: '8px 9px', marginTop: 8, borderRadius: 'var(--radius-lg)',
          background: 'rgba(255,255,255,0.055)',
        }}
      >
        <Avatar name={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: 500, color: 'var(--chrome-ink)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {user?.first_name} {user?.last_name}
          </div>
          <PortalSwitcher />
          <div style={{
            fontSize: 10.5, color: 'var(--chrome-muted)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {isTc ? 'Transaction coordinator' : (user?.roles[0] ?? '')}
          </div>
        </div>
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={chromeIconButton}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={15} />
        </button>
        <button
          onClick={signOut}
          title="Sign out"
          style={chromeIconButton}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <Icon name="log-out" size={15} />
        </button>
      </div>
    </aside>
  );
}

const chromeIconButton: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--chrome-muted)',
  padding: 5,
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  transition: 'background 130ms var(--ease)',
};


// ──────────────────────────────────────────────────────────────────────
// Reusable header for pages — title + subtitle + actions
// ──────────────────────────────────────────────────────────────────────

export function PageHeader({
  title,
  subtitle,
  actions,
  eyebrow,
}: {
  title: string;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /** Small text above the title, e.g. "Pipeline" */
  eyebrow?: string;
}) {
  return (
    // Round G layout fix (Daniel 2026-06-05): the previous version was
    // a single flex row with non-shrinking actions. On pages like
    // Production with 6 action buttons the title/subtitle column got
    // squeezed to ~80px wide, forcing the subtitle to wrap one word
    // per line. Two changes:
    //   - `flexWrap: 'wrap'` lets the actions row drop below the title
    //     on narrow viewports.
    //   - `minWidth: 240` on the title column guarantees a sane
    //     subtitle width before actions wrap.
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        flexWrap: 'wrap',
        marginBottom: 24,
        gap: 16,
      }}
    >
      <div style={{ minWidth: 240, flex: '1 1 240px' }}>
        {eyebrow && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            {eyebrow}
          </div>
        )}
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 30,
            fontWeight: 700,
            color: 'var(--text-heading)',
            margin: 0,
            letterSpacing: '-0.025em',
            lineHeight: 1.1,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 6 }}>{subtitle}</div>
        )}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Reusable Card with optional title + icon
// ──────────────────────────────────────────────────────────────────────

export function ModernCard({
  title,
  icon,
  link,
  actions,
  children,
  padding = 18,
}: {
  title?: string;
  icon?: IconName;
  link?: { to: string; label: string };
  actions?: React.ReactNode;
  children: React.ReactNode;
  padding?: number;
}) {
  const hasHeader = title || link || actions;
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-sm)',
        padding,
      }}
    >
      {hasHeader && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {icon && (
              <span style={{ color: 'var(--text-muted)', flexShrink: 0 }}>
                <Icon name={icon} size={15} />
              </span>
            )}
            {title && (
              <h2
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 15,
                  fontWeight: 600,
                  color: 'var(--text-heading)',
                  margin: 0,
                  letterSpacing: '-0.01em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </h2>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {actions}
            {link && (
              <Link
                to={link.to}
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: 'var(--brand-primary)',
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {link.label}
                <Icon name="chevron-right" size={12} />
              </Link>
            )}
          </div>
        </div>
      )}
      {children}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Avatar
// ──────────────────────────────────────────────────────────────────────

export function Avatar({ name, size = 30 }: { name: string; size?: number }) {
  const initials = name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: 'linear-gradient(135deg, var(--brand-primary-soft) 0%, var(--accent-coral-soft) 100%)',
        color: 'var(--text-heading)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.round(size * 0.36),
        fontWeight: 600,
        flexShrink: 0,
      }}
    >
      {initials || '·'}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Modern button styles
// ──────────────────────────────────────────────────────────────────────

export const ModernButton = {
  primary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    background: 'var(--brand-primary)',
    color: 'var(--text-on-brand)',
    borderRadius: 'var(--radius)',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(31, 29, 61, 0.1)',
  } as React.CSSProperties,
  secondary: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    background: 'var(--bg-surface)',
    color: 'var(--text-heading)',
    borderRadius: 'var(--radius)',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    border: '1px solid var(--border-strong)',
    cursor: 'pointer',
  } as React.CSSProperties,
  ghost: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    background: 'transparent',
    color: 'var(--text-muted)',
    fontSize: 13,
    fontWeight: 500,
    textDecoration: 'none',
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
};
